import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, GripVertical, Upload, Loader2, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { getAdminBlog, createBlog, updateBlog, uploadBlogImage } from '../utils/api';

const emptySection = () => ({ heading: '', paragraphs: [''], bullets: [], numbered: [], images: [] });

const emptyPost = () => ({
  title: '',
  slug: '',
  category: '',
  excerpt: '',
  readTime: '',
  date: '',
  metaTitle: '',
  metaDescription: '',
  keywords: '',
  published: true,
  sections: [emptySection()],
  faqs: [],
});

const normalize = (p) => ({
  ...emptyPost(),
  ...p,
  sections: (p.sections?.length ? p.sections : [emptySection()]).map((s) => ({
    heading: s.heading || '',
    paragraphs: s.paragraphs?.length ? s.paragraphs : [''],
    bullets: s.bullets || [],
    numbered: s.numbered || [],
    images: s.images || [],
  })),
  faqs: p.faqs || [],
});

const ALIGN_OPTS = [
  { key: 'left', label: 'Left', Icon: AlignLeft },
  { key: 'center', label: 'Center', Icon: AlignCenter },
  { key: 'right', label: 'Right', Icon: AlignRight },
];

const SectionImages = ({ images, si, mutateSections }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('Image too large (max 20 MB)'); return; }
    setUploading(true);
    try {
      const res = await uploadBlogImage(file);
      mutateSections((s) => { s[si].images.push({ url: res.absoluteUrl, caption: '', align: 'center' }); });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <Label>Images & Graphs (optional)</Label>
      <p className="text-xs text-slate-500 -mt-1">Max 20 MB · JPG, PNG, WEBP or GIF · choose Left / Center / Right placement per image</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {images.map((im, ii) => (
          <div key={ii} className="border border-slate-200 rounded-lg p-3 bg-white space-y-2" data-testid={`blog-section-${si}-image-${ii}`}>
            <img src={im.url} alt={im.caption || 'blog visual'} className="w-full h-32 object-cover rounded-md border" />
            <Input
              value={im.caption || ''}
              onChange={(e) => mutateSections((s) => { s[si].images[ii].caption = e.target.value; })}
              placeholder="Caption (optional)"
              data-testid={`blog-section-${si}-image-${ii}-caption`}
            />
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                {ALIGN_OPTS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    title={label}
                    onClick={() => mutateSections((s) => { s[si].images[ii].align = key; })}
                    className={`p-1.5 rounded border ${((im.align || 'center') === key) ? 'bg-amber-100 border-amber-400 text-amber-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    data-testid={`blog-section-${si}-image-${ii}-align-${key}`}
                  >
                    <Icon size={14} />
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => mutateSections((s) => s[si].images.splice(ii, 1))} data-testid={`blog-section-${si}-image-${ii}-remove`}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 text-sm px-3 py-2 border border-dashed border-slate-300 rounded-lg hover:bg-slate-50 w-full justify-center text-slate-600 disabled:opacity-60"
        data-testid={`blog-section-${si}-add-image`}
      >
        {uploading ? <><Loader2 className="animate-spin" size={16} /> Uploading…</> : <><ImageIcon size={16} /> Add Image / Graph</>}
      </button>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleSelect} />
    </div>
  );
};

const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [post, setPost] = useState(emptyPost());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = (isEdit ? 'Edit Post' : 'New Post') + ' - Rubicon Finservices';
    if (!isEdit) return;
    getAdminBlog(id)
      .then((p) => setPost(normalize(p)))
      .catch((err) => {
        toast.error('Failed to load post: ' + err.message);
        navigate('/admin/blog');
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const setField = (key, value) => setPost((prev) => ({ ...prev, [key]: value }));

  const mutateSections = (fn) => setPost((prev) => {
    const sections = JSON.parse(JSON.stringify(prev.sections));
    fn(sections);
    return { ...prev, sections };
  });

  const mutateFaqs = (fn) => setPost((prev) => {
    const faqs = JSON.parse(JSON.stringify(prev.faqs));
    fn(faqs);
    return { ...prev, faqs };
  });

  const handleSave = async () => {
    if (!post.title || post.title.trim().length < 2) {
      toast.error('Please enter a title (at least 2 characters).');
      return;
    }
    setSaving(true);
    const payload = {
      ...post,
      sections: post.sections
        .map((s) => ({
          heading: s.heading.trim(),
          paragraphs: s.paragraphs.map((p) => p.trim()).filter(Boolean),
          bullets: s.bullets.filter((b) => (b.text || '').trim()),
          numbered: s.numbered.filter((n) => (n.text || '').trim()),
          images: (s.images || []).filter((im) => (im.url || '').trim()).map((im) => ({
            url: im.url, caption: (im.caption || '').trim(), align: im.align || 'center',
          })),
        }))
        .filter((s) => s.heading || s.paragraphs.length || s.bullets.length || s.numbered.length || s.images.length),
      faqs: post.faqs.filter((f) => (f.q || '').trim() && (f.a || '').trim()),
    };
    try {
      if (isEdit) {
        await updateBlog(id, payload);
        toast.success('Post updated');
      } else {
        await createBlog(payload);
        toast.success('Post created');
      }
      navigate('/admin/blog');
    } catch (err) {
      toast.error((isEdit ? 'Update' : 'Create') + ' failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-editor-page">
      <header className="bg-white shadow-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => navigate('/admin/blog')} data-testid="blog-editor-back">
              <ArrowLeft size={16} /> Back
            </Button>
            <h1 className="text-xl font-bold text-slate-900">{isEdit ? 'Edit Post' : 'New Post'}</h1>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2" data-testid="blog-editor-save">
            <Save size={16} /> {saving ? 'Saving...' : 'Save Post'}
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        {/* Basics */}
        <Card>
          <CardHeader><CardTitle>Article Basics</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" data-testid="blog-field-title" value={post.title} onChange={(e) => setField('title', e.target.value)} placeholder="e.g. How to Start a SIP in Kolkata" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="slug">URL Slug</Label>
                <Input id="slug" data-testid="blog-field-slug" value={post.slug} onChange={(e) => setField('slug', e.target.value)} placeholder="auto-generated from title if left blank" />
                <p className="text-xs text-slate-500 mt-1">Page will be at /blog/{post.slug || '(from title)'}</p>
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Input id="category" data-testid="blog-field-category" value={post.category} onChange={(e) => setField('category', e.target.value)} placeholder="e.g. SIP Basics" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="readTime">Read Time</Label>
                <Input id="readTime" value={post.readTime} onChange={(e) => setField('readTime', e.target.value)} placeholder="e.g. 6 min read" />
              </div>
              <div>
                <Label htmlFor="date">Publish Date</Label>
                <Input id="date" type="date" value={post.date} onChange={(e) => setField('date', e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="excerpt">Summary / Excerpt</Label>
              <Textarea id="excerpt" data-testid="blog-field-excerpt" value={post.excerpt} onChange={(e) => setField('excerpt', e.target.value)} placeholder="A short intro shown on the blog list and top of the article." rows={3} />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Switch id="published" checked={post.published} onCheckedChange={(v) => setField('published', v)} data-testid="blog-field-published" />
              <Label htmlFor="published" className="cursor-pointer">{post.published ? 'Published (visible on website)' : 'Draft (hidden)'}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Body sections */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Article Body</CardTitle>
            <Button variant="outline" size="sm" onClick={() => mutateSections((s) => s.push(emptySection()))} className="flex items-center gap-1" data-testid="blog-add-section">
              <Plus size={14} /> Add Section
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {post.sections.map((section, si) => (
              <div key={si} className="border-2 border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50/50" data-testid={`blog-section-${si}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-500"><GripVertical size={14} /> Section {si + 1}</span>
                  {post.sections.length > 1 && (
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => mutateSections((s) => s.splice(si, 1))}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
                <div>
                  <Label>Heading</Label>
                  <Input value={section.heading} onChange={(e) => mutateSections((s) => { s[si].heading = e.target.value; })} placeholder="Section heading" data-testid={`blog-section-heading-${si}`} />
                </div>

                {/* Paragraphs */}
                <div className="space-y-2">
                  <Label>Paragraphs</Label>
                  {section.paragraphs.map((para, pi) => (
                    <div key={pi} className="flex gap-2">
                      <Textarea value={para} onChange={(e) => mutateSections((s) => { s[si].paragraphs[pi] = e.target.value; })} rows={2} placeholder="Write a paragraph..." data-testid={`blog-section-${si}-para-${pi}`} />
                      <Button variant="ghost" size="sm" className="text-red-600 flex-shrink-0" onClick={() => mutateSections((s) => { s[si].paragraphs.splice(pi, 1); if (!s[si].paragraphs.length) s[si].paragraphs.push(''); })}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => mutateSections((s) => s[si].paragraphs.push(''))} className="flex items-center gap-1">
                    <Plus size={12} /> Add Paragraph
                  </Button>
                </div>

                {/* Bullets */}
                <div className="space-y-2">
                  <Label>Bullet List (optional)</Label>
                  {section.bullets.map((b, bi) => (
                    <div key={bi} className="flex gap-2 items-start">
                      <Input className="w-1/3" value={b.title || ''} onChange={(e) => mutateSections((s) => { s[si].bullets[bi].title = e.target.value; })} placeholder="Bold lead (optional)" />
                      <Input value={b.text || ''} onChange={(e) => mutateSections((s) => { s[si].bullets[bi].text = e.target.value; })} placeholder="Bullet text" />
                      <Button variant="ghost" size="sm" className="text-red-600 flex-shrink-0" onClick={() => mutateSections((s) => s[si].bullets.splice(bi, 1))}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => mutateSections((s) => s[si].bullets.push({ title: '', text: '' }))} className="flex items-center gap-1">
                    <Plus size={12} /> Add Bullet
                  </Button>
                </div>

                {/* Numbered */}
                <div className="space-y-2">
                  <Label>Numbered Steps (optional)</Label>
                  {section.numbered.map((n, ni) => (
                    <div key={ni} className="flex gap-2 items-start">
                      <Input className="w-1/3" value={n.title || ''} onChange={(e) => mutateSections((s) => { s[si].numbered[ni].title = e.target.value; })} placeholder="Bold lead (optional)" />
                      <Input value={n.text || ''} onChange={(e) => mutateSections((s) => { s[si].numbered[ni].text = e.target.value; })} placeholder="Step text" />
                      <Button variant="ghost" size="sm" className="text-red-600 flex-shrink-0" onClick={() => mutateSections((s) => s[si].numbered.splice(ni, 1))}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => mutateSections((s) => s[si].numbered.push({ title: '', text: '' }))} className="flex items-center gap-1">
                    <Plus size={12} /> Add Step
                  </Button>
                </div>

                {/* Images & Graphs */}
                <SectionImages images={section.images || []} si={si} mutateSections={mutateSections} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* FAQs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>FAQs</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Shown as an accordion and added to Google FAQ schema for SEO.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => mutateFaqs((f) => f.push({ q: '', a: '' }))} className="flex items-center gap-1" data-testid="blog-add-faq">
              <Plus size={14} /> Add FAQ
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {post.faqs.length === 0 && <p className="text-sm text-slate-400">No FAQs added.</p>}
            {post.faqs.map((f, fi) => (
              <div key={fi} className="border-2 border-slate-200 rounded-lg p-4 space-y-2" data-testid={`blog-faq-${fi}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-500">FAQ {fi + 1}</span>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => mutateFaqs((arr) => arr.splice(fi, 1))}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                <Input value={f.q} onChange={(e) => mutateFaqs((arr) => { arr[fi].q = e.target.value; })} placeholder="Question" />
                <Textarea value={f.a} onChange={(e) => mutateFaqs((arr) => { arr[fi].a = e.target.value; })} placeholder="Answer" rows={2} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* SEO */}
        <Card>
          <CardHeader><CardTitle>SEO (optional)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="metaTitle">Meta Title</Label>
              <Input id="metaTitle" value={post.metaTitle} onChange={(e) => setField('metaTitle', e.target.value)} placeholder="Defaults to the article title if blank" />
            </div>
            <div>
              <Label htmlFor="metaDescription">Meta Description</Label>
              <Textarea id="metaDescription" value={post.metaDescription} onChange={(e) => setField('metaDescription', e.target.value)} rows={2} placeholder="~150 characters shown in Google results" />
            </div>
            <div>
              <Label htmlFor="keywords">Keywords</Label>
              <Input id="keywords" value={post.keywords} onChange={(e) => setField('keywords', e.target.value)} placeholder="comma, separated, keywords" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end pb-10">
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2">
            <Save size={16} /> {saving ? 'Saving...' : 'Save Post'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
